import { BAutocomplete, BDropdown, BTooltip } from 'buefy';
import { appendedMenuPosition } from './layoutReads';

const FALLBACK_MENU_HEIGHT = 200;

/**
 * Global fix for the orientation of Buefy autocomplete dropdowns (also used by
 * b-taginput), especially inside Modal.show() popups.
 *
 * Stock Buefy decides "open upward or downward" by measuring the dropdown
 * menu's OWN bounding rect. With `append-to-body` that rect is wherever the
 * menu was last absolutely positioned — often stale (modal open animation,
 * page scroll, previous open). Worse, the decision feeds back into itself:
 * once the menu flips above the trigger and clips past the top of the screen,
 * rect.top < 0 keeps reporting "not in viewport", so it stays stuck upward
 * even with ample room below.
 *
 * The decision must instead be based on the space around the TRIGGER, which is
 * stable and idempotent: open downward unless the menu doesn't fit below and
 * there is more room above.
 */
BAutocomplete.methods.calcDropdownInViewportVertical = function () {
    this.$nextTick(() => {
        const trigger = this.$parent?.$data?._isTaginput ? this.$parent.$el : this.$refs.input?.$el;
        if (!trigger || this.$refs.dropdown == null) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const menuHeight = this.$refs.dropdown.clientHeight || FALLBACK_MENU_HEIGHT;
        const spaceBelow = viewportHeight - rect.bottom;

        this.isListInViewportVertically = spaceBelow >= menuHeight || spaceBelow >= rect.top;

        if (this.appendToBody) {
            this.updateAppendToBody();
        }
    });
};

/**
 * Results usually arrive async after the dropdown is already open, changing
 * the menu height. Re-run the orientation/position calculation when the data
 * changes so an open menu never drifts off the trigger or off screen.
 */
const stockDataWatcher = BAutocomplete.watch.data;
BAutocomplete.watch.data = function (...args) {
    stockDataWatcher.apply(this, args);
    if (this.isActive && this.dropdownPosition === 'auto') {
        this.calcDropdownInViewportVertical();
    }
};

/**
 * Keep body-appended menus glued to their trigger when an ANCESTOR scroll
 * container scrolls — not only the window.
 *
 * Buefy repositions an `append-to-body` menu on `window` scroll for autocomplete
 * (and for b-dropdown / datepicker / timepicker: not at all). But `scroll`
 * events don't bubble, so scrolling an inner overflow container — a modal body,
 * a split-pane, the task edit form — never reaches those window listeners. The
 * absolutely-positioned menu stays put while the field scrolls away, so it
 * visibly detaches from the trigger.
 *
 * Listening in the CAPTURE phase catches scroll from every ancestor. On each
 * scroll we re-run Buefy's own `updateAppendToBody()` for the open menu(s),
 * re-gluing them to the trigger. One shared listener, rAF-throttled, iterates
 * only mounted append-to-body menus and repositions whichever is active.
 *
 * When the trigger is scrolled entirely out of its own scroll viewport, gluing
 * would float the menu over whatever now occupies that space (a sticky header, a
 * section title). So while the trigger is fully clipped we hide the menu instead
 * of moving it, and reveal + re-glue it once the field scrolls back into view.
 */
const appendedMenus = new Set();
let repositionFrame = null;

function triggerElFor(menu) {
    const refs = menu.$refs || {};
    if (refs.trigger) {
        return refs.trigger; // b-dropdown (also datepicker/timepicker calendars)
    }
    if (menu.$parent?.$data?._isTaginput) {
        return menu.$parent.$el; // b-taginput wraps the autocomplete
    }
    return refs.input?.$el || null; // b-autocomplete
}

/**
 * True when `el` is scrolled completely past the top/bottom edge of the
 * viewport or of any clipping (overflow) ancestor — i.e. not visible anywhere.
 */
function isTriggerClipped(el) {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (rect.bottom <= 0 || rect.top >= viewportHeight) {
        return true;
    }
    let ancestor = el.parentElement;
    while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
        const overflowY = getComputedStyle(ancestor).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
            const clip = ancestor.getBoundingClientRect();
            if (rect.bottom <= clip.top + 1 || rect.top >= clip.bottom - 1) {
                return true;
            }
        }
        ancestor = ancestor.parentElement;
    }
    return false;
}

function repositionOpenMenus() {
    repositionFrame = null;
    appendedMenus.forEach(menu => {
        if (!menu.isActive || !menu.appendToBody || !menu.$data?._bodyEl) {
            return;
        }
        const trigger = triggerElFor(menu);
        if (trigger && isTriggerClipped(trigger)) {
            const bodyEl = menu.$data._bodyEl;
            bodyEl.style.visibility = 'hidden';
            bodyEl.style.pointerEvents = 'none';
            return;
        }
        // updateAppendToBody re-glues to the trigger and clears any stale
        // "scrolled-off" hidden state (see the patch below).
        menu.updateAppendToBody();
    });
}

function scheduleReposition() {
    if (repositionFrame != null || appendedMenus.size === 0) {
        return;
    }
    repositionFrame = requestAnimationFrame(repositionOpenMenus);
}

if (typeof window !== 'undefined') {
    window.addEventListener('scroll', scheduleReposition, { capture: true, passive: true });
}

function trackAppendedMenu(component) {
    const stockCreated = component.created;
    const stockBeforeUnmount = component.beforeUnmount;
    const stockUpdateAppendToBody = component.methods.updateAppendToBody;

    component.created = function (...args) {
        stockCreated?.apply(this, args);
        appendedMenus.add(this);
    };

    component.beforeUnmount = function (...args) {
        appendedMenus.delete(this);
        stockBeforeUnmount?.apply(this, args);
    };

    // Buefy repositions on (re)open too, and b-dropdown reuses one _bodyEl for
    // its whole lifetime — so a menu hidden for being scrolled off, then closed,
    // must not reopen invisible. Reveal it whenever Buefy re-glues to the
    // trigger; the scroll handler re-hides on the next frame if still clipped.
    component.methods.updateAppendToBody = function (...args) {
        stockUpdateAppendToBody.apply(this, args);
        const bodyEl = this.$data?._bodyEl;
        if (bodyEl && bodyEl.style.visibility === 'hidden') {
            bodyEl.style.visibility = '';
            bodyEl.style.pointerEvents = '';
        }
    };
}

trackAppendedMenu(BAutocomplete);
trackAppendedMenu(BDropdown);

/**
 * Position a body-appended menu or tooltip only while it can be seen.
 *
 * Stock Buefy measures the trigger as soon as the component mounts, and a
 * tooltip measures again from a ResizeObserver on its parent. Each measure is a
 * `getBoundingClientRect()` right after class and style writes, so each one
 * forces a layout of the whole page. In a list whose rows carry a tooltip or a
 * menu, a page of fifty new rows meant fifty layouts of a table already holding
 * hundreds — and every column resize re-measured every row already there. The
 * list froze for 300 to 600 ms each time a page arrived, under the reader's
 * scroll.
 *
 * A closed menu has no position worth knowing. Opening one already re-positions
 * it (the `isActive` watcher of each component), so the measure is skipped until
 * then — and run once more on the close, which is what resets the z-index Buefy
 * raised for the open state. See `appendedMenuPosition` (utils/layoutReads.js).
 */
function positionOnlyWhenOpen(component) {
    const stockUpdateAppendToBody = component.methods.updateAppendToBody;

    component.methods.updateAppendToBody = function (...args) {
        const { position, positionedOpen } = appendedMenuPosition({
            isActive: this.isActive,
            inline: this.inline,
            hoverable: this.hoverable,
            always: this.always,
            positionedOpen: this.$data.__positionedOpen
        });

        if (!position) {
            return;
        }

        this.$data.__positionedOpen = positionedOpen;
        stockUpdateAppendToBody.apply(this, args);
    };
}

// Not BAutocomplete: it re-positions on opening only with `dropdown-position`
// "auto", and otherwise relies on the measure taken at mount.
positionOnlyWhenOpen(BDropdown);
positionOnlyWhenOpen(BTooltip);

/**
 * A tooltip sets itself up the first time it opens, not when it mounts.
 *
 * Stock Buefy, for every tooltip on screen, adds a click and a keyup listener
 * on the document and — with `append-to-body` — moves its content into the
 * body, watches its parent with a ResizeObserver and listens to the window's
 * resize. A list puts one in every date cell: 377 contacts meant 755 tooltips,
 * 1 500 elements appended to the body and every click on the page running 1 500
 * handlers, for labels nobody had hovered yet.
 *
 * The document listeners only serve an open tooltip (closing it on an outside
 * click or on Escape), so they live while it is open. The body placement is
 * done on the first opening, just before Buefy positions it. A tooltip shown
 * without being opened (`always`) is set up at once, as stock.
 */
const stockTooltipMounted = BTooltip.mounted;
const stockTooltipBeforeUnmount = BTooltip.beforeUnmount;
const stockTooltipActiveWatcher = BTooltip.watch.isActive;

function setUpTooltipBody(vm) {
    if (vm.$data.__bodySetUp) {
        return;
    }
    vm.$data.__bodySetUp = true;
    stockTooltipMounted.call(vm);
}

BTooltip.created = function () {
    // Stock only adds the document listeners — added on opening instead.
};

BTooltip.mounted = function () {
    if (this.always || !this.appendToBody) {
        setUpTooltipBody(this);
    }
};

BTooltip.watch.isActive = function (active, ...rest) {
    if (active) {
        setUpTooltipBody(this);
        document.addEventListener('click', this.clickedOutside);
        document.addEventListener('keyup', this.keyPress);
    } else {
        document.removeEventListener('click', this.clickedOutside);
        document.removeEventListener('keyup', this.keyPress);
    }

    return stockTooltipActiveWatcher.call(this, active, ...rest);
};

BTooltip.beforeUnmount = function (...args) {
    // Stock removes the body element unconditionally; a tooltip never opened
    // has none, and `removeElement(undefined)` throws.
    if (this.appendToBody && !this.$data._bodyEl) {
        this.$data._bodyEl = document.createElement('div');
    }

    return stockTooltipBeforeUnmount?.apply(this, args);
};
